/**
 * @file employeeNavConfig.ts
 * @brief Mega-menu nav for the employee Boardroom web app (`/app`).
 *
 * @author DES
 * @date 28-07-2026
 */

import {
  AudioLines,
  CalendarClock,
  CalendarDays,
  CreditCard,
  House,
  Mic,
  PenLine,
  UserRound,
} from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { TodoTick } from "@/components/icons/TodoTick";
import { SessionTurns } from "@/components/icons/SessionTurns";
import type { NavShellConfig } from "@/layouts/components/nav-types";

/**
 * @param notesEnabled Effective Personal Notes access from `/me`. Undefined
 *   while entitlements are still loading — shown in that case, so the entry
 *   never flash-hides on a slow bootstrap.
 */
export function buildEmployeeNavConfig(
  subtitle: string,
  notesEnabled?: boolean,
  /** Interpretation access; undefined while loading (shown, as Notes is). */
  interpretationEnabled?: boolean,
  /** Live interpretation on the web: the pilot list (`features/interpret/pilot.ts`). */
  webInterpretEnabled?: boolean,
  /** A personal user pays for themselves; a company member's company pays (17-10-2026). */
  personalBilling = false,
): NavShellConfig {
  return {
    homeTo: "/app",
    scopeTitle: "Meeting Intel",
    scopeSubtitle: subtitle,
    highlights: [
      // Home first, as on the phone (30-09-2026).
      {
        to: "/app",
        label: "Home",
        description: "Recent meetings, sessions and notes",
        icon: House,
        end: true,
      },
      {
        to: "/app/meetings/new",
        label: "New boardroom",
        description: "Record a meeting from this laptop",
        icon: Mic,
      },
      {
        to: "/app/meetings/scheduled",
        // The page's own name (DES 01-10-2026).
        label: "Scheduled Meetings",
        description: "Scheduled · Start now",
        icon: CalendarClock,
      },
      {
        to: "/app/meetings",
        // "Boardroom": the phone's Boardrooms list (DES 01-10-2026: "what
        // is the point of recordings in the menu").
        label: "Boardroom",
        description: "Your meetings and their intel",
        icon: BoardroomTable, // DES 08-10-2026: app icon, was Presentation
        end: true,
      },
      // To-dos from every meeting (12-10-2026).
      {
        to: "/app/todos",
        label: "My to-dos",
        description: "Open, overdue and done, from all your meetings",
        icon: TodoTick,
      },
      // Live interpretation from the laptop, a pilot (01-10-2026).
      ...(interpretationEnabled === false || webInterpretEnabled !== true
        ? []
        : [
            {
              to: "/app/interpret",
              label: "Interpreter",
              description: "Interpret a conversation from this laptop",
              icon: AudioLines,
            },
          ]),
      // Their own interpretation sessions (01-10-2026).
      ...(interpretationEnabled === false
        ? []
        : [
            {
              to: "/app/sessions",
              label: "Sessions",
              description: "Your interpretation sessions",
              icon: SessionTurns, // DES 08-10-2026: app icon, was Languages
            },
            {
              to: "/app/schedule",
              label: "Diary",
              description: "Sessions booked for you",
              icon: CalendarDays,
            },
          ]),
      // Personal, not company: the meeting agenda you write for yourself.
      ...(notesEnabled === false
        ? []
        : [
            {
              to: "/app/notes",
              label: "Notes",
              description: "Write a meeting agenda · your own notes",
              icon: PenLine, // DES 08-10-2026: app icon, was NotebookPen
            },
          ]),
    ],
    groups: [
      {
        id: "account",
        label: "Account",
        items: [
          {
            to: "/app/account",
            label: "Profile",
            description: "Name, photo, password",
            icon: UserRound,
          },
          // Plan, usage, cards and receipts (17-10-2026).
          ...(personalBilling
            ? [
                {
                  to: "/app/billing/plan",
                  label: "Billing",
                  description: "Plan, usage, cards, invoices",
                  icon: CreditCard,
                },
              ]
            : []),
        ],
      },
    ],
  };
}
